import {
  COIN_JACKPOT_SYMBOL_ID,
  JACKPOT_SKIN_RANK,
  cellZIndex,
  getSymbolZIndex,
} from "../../SymbolData";
import { SOUND_NAME } from "../../sounds";

/**
 * A jackpot coin (`cj`) wears its tier's skin and stacks by tier.
 *
 * The skin is on the round, per cell (`round.jackpotCoinSkins["reel_row"]`,
 * PopiPlay RGS markJackpotCoins). It goes on the cell's pooled `cj` spine
 * before that spine plays — on a landing, AND on the idle a reload starts
 * without ever landing the coin (otherwise the idle shows the default skin:
 * a plain coin with no tier word). A Mini never stacks over a Grand
 * (JACKPOT_SKIN_RANK).
 *
 * @param {object} overlayController  the reel's overlay controller
 * @param {object} round              the board's current round
 * @param {number} reelIndex
 * @param {number} symbolIndex        the row
 * @returns {string|undefined} the skin worn, if any
 */
export const wearJackpotCoinSkin = (overlayController, round, reelIndex, symbolIndex) => {
  const skin = round?.jackpotCoinSkins?.[`${reelIndex}_${symbolIndex}`];
  const cj = overlayController?.prePositionedSpines?.get(
    `${symbolIndex}_${COIN_JACKPOT_SYMBOL_ID}`,
  );
  // Already wearing it (e.g. mid-wait since the anticipation): leave the
  // playing spine's slots alone.
  if (skin && cj?.skeleton?.skin?.name !== skin && cj?.skeleton?.data?.findSkin(skin)) {
    cj.skeleton.setSkinByName(skin);
    cj.skeleton.setSlotsToSetupPose();
  }
  const box = overlayController?.overlaySymbolRefs?.[symbolIndex];
  if (box && !box.destroyed) {
    box.zIndex = cellZIndex(
      getSymbolZIndex(COIN_JACKPOT_SYMBOL_ID) + (JACKPOT_SKIN_RANK[skin] ?? 0),
      reelIndex,
      symbolIndex,
    );
  }
  return skin;
};

// ── A jackpot coin's own landing (batch 3, 2026-10-22) ── it plays its tier's
// sound instead of coin_land. The Royal has none: it keeps coin_land.
const JACKPOT_LAND_SOUND = {
  Jp_mini: SOUND_NAME.mini_coin,
  Jp_minor: SOUND_NAME.minor_coin,
  Jp_midi: SOUND_NAME.midi_coin,
  Jp_major: SOUND_NAME.major_coin,
  Jp_grand: SOUND_NAME.grand_coin,
};

/**
 * The landing sound of the highest jackpot coin among `skins` (Jp_* names,
 * as round.jackpotCoinSkins holds them) — one sound per landing, like every
 * other landing. Null when none of them has one.
 *
 * @param {Array<string|null>} skins
 * @returns {string|null}
 */
export const jackpotLandSound = (skins) => {
  let best = null;
  let bestRank = -1;
  for (const skin of skins || []) {
    const sound = JACKPOT_LAND_SOUND[skin];
    const rank = JACKPOT_SKIN_RANK[skin] ?? -1;
    if (sound && rank > bestRank) {
      best = sound;
      bestRank = rank;
    }
  }
  return best;
};
